import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, remove } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import OpeningStock from './AddOpeningStock';

const StockList = () => {
  const [showForm, setShowForm] = useState(false);
  const [stockEntries, setStockEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        const encodedEmail = encodeEmailForPath(user.email);
        const stocksRef = ref(database, `users/${encodedEmail}/openingStock`);
        onValue(stocksRef, (snapshot) => {
          const data = snapshot.val();
          const stockList = data
            ? Object.keys(data).map((key) => ({
                id: key,
                ...data[key],
              }))
            : [];
          setStockEntries(stockList);
        }, (error) => {
          console.error("Error fetching stock entries:", error);
          setErrorMessage(`Failed to load stock entries: ${error.message}`);
        });
      }
    });
    return () => unsubscribe();
  }, []);

  const handleAddStock = () => {
    setSelectedEntry(null);
    setShowForm(true);
  };

  const handleBack = () => {
    setShowForm(false);
    setSelectedEntry(null);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setShowForm(true);
  };

  const handleDeleteEntry = async (stockId) => {
    if (!currentUser || !currentUser.email) {
      setErrorMessage("You must be logged in to delete data!");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this stock entry?")) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    try {
      const stockRef = ref(database, `users/${encodedEmail}/openingStock/${stockId}`);
      await remove(stockRef);
      setSuccessMessage("Stock entry deleted successfully!");
      setErrorMessage("");
    } catch (error) {
      console.error("Error deleting stock:", error);
      setErrorMessage(`Failed to delete stock: ${error.message}`);
      setSuccessMessage("");
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedEntry(null);
    setSuccessMessage("Operation completed successfully!");
    setErrorMessage("");
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedEntry(null);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    tableContainer: {
    
      borderRadius: '16px',
      overflow: 'hidden',
  

    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e2e8f0',
    },
    heading: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    button: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    backButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    th: {
      padding: '1rem 0.75rem',
      textAlign: 'left',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    td: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      marginRight: '0.5rem',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
    },
    messageSuccess: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    messageError: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
      border: '1px solid #fecaca',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  };

  return (
    <div style={styles.container}>
      {successMessage && (
        <div style={styles.messageSuccess}>
          <span>✅</span>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div style={styles.messageError}>
          <span>❌</span>
          {errorMessage}
        </div>
      )}
      <div style={styles.tableContainer}>
        <div style={styles.header}>
          <h1 style={styles.heading}>Stock Entries</h1>
          {showForm ? (
            <button
              style={styles.backButton}
              onClick={handleBack}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(107, 114, 128, 0.3)';
              }}
            >
              Back
            </button>
          ) : (
            <button
              style={styles.button}
              onClick={handleAddStock}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              Add Opening Stock
            </button>
          )}
        </div>

        {showForm ? (
          <OpeningStock
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
            initialData={selectedEntry}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>REF NO</th>
                  <th style={styles.th}>REF DATE</th>
                  <th style={styles.th}>ACCOUNT </th>
                  <th style={styles.th}>LOCATION</th>
                  <th style={styles.th}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {stockEntries.length > 0 ? (
                  stockEntries.map((entry, index) => (
                    <tr
                      key={entry.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <td style={styles.td}>{entry.refNo}</td>
                      <td style={styles.td}>{entry.refDate}</td>
                      <td style={styles.td}>{entry.account}</td>
                      <td style={styles.td}>{entry.location}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.editButton}
                          onClick={() => handleEditEntry(entry)}
                          title="Edit Entry"
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          style={styles.deleteButton}
                          onClick={() => handleDeleteEntry(entry.id)}
                          title="Delete Entry"
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={styles.emptyState}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📦</div>
                        <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Stock Entries Found</h3>
                        <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first stock entry!</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockList;